import React, { Component } from "react";
import _ from "lodash";
import { withTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import QualityAttributeBadges from "../common/qualityAttributeBadges";
import OrderCell from "../common/orderCell";
import TradeConfirmationModal from "../tradeConfirmations/tradeConfirmationModal";
import { formatDateTime } from "../../common/services/utilities";
import { getTradeConfirmationStatuses } from "../../common/services/marketdata/tradeConfirmationService";
import "./marketTrades.css";

class MarketTrades extends Component {
  state = {
    columns: [],
    statusesByTradeId: {},
    selectedTrade: null,
    selectedActingOrganizationId: null,
    showConfirmationModal: false,
  };

  componentDidMount() {
    this.setState({ columns: this.mapToColumns() });
    this.loadTradeConfirmationStatuses();
  }

  componentDidUpdate(previousProps) {
    if (
      !_.isEqual(previousProps.marketTrades, this.props.marketTrades) ||
      !_.isEqual(previousProps.organizationTrades, this.props.organizationTrades) ||
      !_.isEqual(previousProps.userOrganizationIds, this.props.userOrganizationIds)
    ) {
      this.setState({ columns: this.mapToColumns() });
      this.loadTradeConfirmationStatuses();
    }

    if (
      !_.isEqual(
        previousProps.tradeConfirmationNotification,
        this.props.tradeConfirmationNotification
      )
    ) {
      this.handleTradeConfirmationNotification(
        this.props.tradeConfirmationNotification
      );
    }
  }

  getOrganizationTrade = (trade) => {
    return (this.props.organizationTrades || []).find(
      (organizationTrade) => organizationTrade.tradeId === trade?.tradeId
    );
  };

  getActingOrganizationId = (trade) => {
    if (!trade) return null;

    const organizationTrade = this.getOrganizationTrade(trade);
    const direction = organizationTrade?.orderDirection || organizationTrade?.OrderDirection;

    if (direction === "Buy" || direction === "Bid") {
      return trade.buyerOrganizationId || organizationTrade?.organizationId;
    }

    if (direction === "Sell" || direction === "Offer") {
      return trade.sellerOrganizationId || organizationTrade?.organizationId;
    }

    const userOrganizationIds = this.props.userOrganizationIds || [];
    if (userOrganizationIds.includes(trade.buyerOrganizationId)) return trade.buyerOrganizationId;
    if (userOrganizationIds.includes(trade.sellerOrganizationId)) return trade.sellerOrganizationId;

    return organizationTrade?.organizationId || null;
  };

  getPartyTrades = () => {
    return (this.props.marketTrades || [])
      .map((trade) => ({ trade, actingOrganizationId: this.getActingOrganizationId(trade) }))
      .filter((context) => !!context.trade?.tradeId && !!context.actingOrganizationId);
  };


  mapStatusesByTradeId = (statuses) => {
    return (statuses || []).reduce((statusesByTradeId, status) => {
      statusesByTradeId[status.engineTradeId] = status;
      statusesByTradeId[this.normalizeId(status.engineTradeId)] = status;
      return statusesByTradeId;
    }, {});
  };

  loadTradeConfirmationStatuses = async () => {
    const partyTrades = this.getPartyTrades();

    if (!partyTrades.length) {
      this.setState({ statusesByTradeId: {} });
      return;
    }

    try {
      const statuses = await getTradeConfirmationStatuses(
        _.uniq(partyTrades.map((context) => context.trade.tradeId)),
        _.uniq(partyTrades.map((context) => context.actingOrganizationId))
      );
      this.setState({ statusesByTradeId: this.mapStatusesByTradeId(statuses), columns: this.mapToColumns() });
    } catch (ex) {
      console.error("Failed to load trade confirmation statuses", ex);
      toast.error(this.props.t("TradeConfirmationStatusLoadFailed"));
    }
  };


  normalizeId = (value) => {
    return value ? value.toString().toLowerCase() : null;
  };

  getNotificationValue = (notification, camelCaseName, pascalCaseName) => {
    return notification?.[camelCaseName] ?? notification?.[pascalCaseName];
  };

  normalizeTradeConfirmationNotification = (notification) => {
    if (!notification) return null;

    const engineTradeId = this.getNotificationValue(
      notification,
      "engineTradeId",
      "EngineTradeId"
    );

    if (!engineTradeId) return null;

    return {
      engineTradeId,
      tradeConfirmationId: this.getNotificationValue(
        notification,
        "tradeConfirmationId",
        "TradeConfirmationId"
      ),
      lifecycleStatus: this.getNotificationValue(
        notification,
        "lifecycleStatus",
        "LifecycleStatus"
      ),
      lastUpdatedByOrganizationId: this.getNotificationValue(
        notification,
        "lastUpdatedByOrganizationId",
        "LastUpdatedByOrganizationId"
      ),
      eventId: this.getNotificationValue(notification, "eventId", "EventId"),
      exists: true,
      canInitiate: false,
      canView: true,
    };
  };

  getActingOrganizationIds = () => {
    return _.uniq(
      this.getPartyTrades()
        .map((context) => this.normalizeId(context.actingOrganizationId))
        .filter(Boolean)
    );
  };

  handleTradeConfirmationNotification = (notification) => {
    const status = this.normalizeTradeConfirmationNotification(notification);
    if (!status) return;

    const engineTradeId = this.normalizeId(status.engineTradeId);
    const isVisiblePartyTrade = this.getPartyTrades().some(
      (context) => this.normalizeId(context.trade.tradeId) === engineTradeId
    );

    if (!isVisiblePartyTrade) return;

    const actingOrganizationIds = this.getActingOrganizationIds();
    const lastUpdatedByOrganizationId = this.normalizeId(
      status.lastUpdatedByOrganizationId
    );
    const isMyTurn =
      status.lifecycleStatus === "Pending" &&
      actingOrganizationIds.some(
        (organizationId) => organizationId !== lastUpdatedByOrganizationId
      );

    this.setState((previousState) => ({
      statusesByTradeId: {
        ...previousState.statusesByTradeId,
        [status.engineTradeId]: {
          ...status,
          canRespond: isMyTurn,
        },
        [engineTradeId]: {
          ...status,
          canRespond: isMyTurn,
        },
      },
      columns: this.mapToColumns(),
    }));

    if (isMyTurn) {
      toast.info(this.props.t("TradeConfirmationRequiresReview"));
    }
  };

  openTradeConfirmation = (trade) => {
    const actingOrganizationId = this.getActingOrganizationId(trade);
    if (!actingOrganizationId) return;

    this.setState({
      selectedTrade: trade,
      selectedActingOrganizationId: actingOrganizationId,
      showConfirmationModal: true,
    });
  };

  closeTradeConfirmation = () => {
    this.setState({
      selectedTrade: null,
      selectedActingOrganizationId: null,
      showConfirmationModal: false,
    });
  };

  getConfirmationButtonState = (trade) => {
    const status = this.state.statusesByTradeId[trade.tradeId] || this.state.statusesByTradeId[this.normalizeId(trade.tradeId)];
    const actingOrganizationId = this.getActingOrganizationId(trade);

    if (!actingOrganizationId) return null;

    if (!status || !status.exists || status.lifecycleStatus === "NotStarted") {
      return { label: this.props.t("InitiateConfirmation"), disabled: false, variant: "outline-primary" };
    }

    if (status.lifecycleStatus === "Pending") {
      const canRespond = !status.lastUpdatedByOrganizationId || this.normalizeId(status.lastUpdatedByOrganizationId) !== this.normalizeId(actingOrganizationId);
      return canRespond
        ? { label: this.props.t("Respond"), disabled: false, variant: "primary" }
        : { label: this.props.t("AwaitingCounter"), disabled: true, variant: "outline-secondary" };
    }

    return { label: this.props.t(status.lifecycleStatus), disabled: false, variant: "outline-secondary" };
  };

  renderConfirmationAction = (params) => {
    const trade = params.data;
    const buttonState = this.getConfirmationButtonState(trade);

    if (!buttonState) return null;

    return (
      <div className="market-trades-action-cell">
        <Button
          size="sm"
          className="market-trades-action-button"
          variant={buttonState.variant}
          disabled={buttonState.disabled}
          onClick={() => this.openTradeConfirmation(trade)}
        >
          {buttonState.label}
        </Button>
      </div>
    );
  };

  mapToColumns() {
    const { organizationTrades } = this.props;
    const getOrganizationTrade = (tradeId) =>
      organizationTrades.find((trade) => trade.tradeId === tradeId);
    const getTradeSide = (tradeId) => {
      const organizationTrade = getOrganizationTrade(tradeId);
      if (!organizationTrade) {
        return undefined;
      }

      return organizationTrade.orderDirection === "Buy" ? "Bid" : "Offer";
    };
    const isOrganizationTrade = (tradeId) => Boolean(getOrganizationTrade(tradeId));

    const columns = [
      {
        name: "tradeId",
        field: "tradeId",
        headerName: this.props.t("TradeId"),
        flex: 3,
      },
      {
        name: "marketName",
        field: "marketName",
        headerName: this.props.t("MarketName"),
        flex: 2,
      },
      {
        name: "termName",
        field: "termName",
        headerName: this.props.t("TermName"),
        flex: 1.5,
      },
      {
        name: "qualityAttributeValues",
        headerName: this.props.t("QualityAttributes"),
        flex: 1.5,
        cellRenderer: (params) => (
          <QualityAttributeBadges
            qualityAttributeValues={params.data?.qualityAttributeValues}
          />
        ),
      },
      {
        name: "tradeDate",
        headerName: this.props.t("TradeDate"),
        flex: 2,
        cellRenderer: (params) => {
          return (
            <span>
              {formatDateTime(
                params.data?.tradeDate,
                Intl.DateTimeFormat().resolvedOptions().timeZone,
                " ",
              )}
            </span>
          );
        },
      },
      {
        name: "price",
        headerName: this.props.t("Price"),
        flex: 1,
        cellRenderer: (params) => {
          return (
            <OrderCell
              value={params.data?.price}
              type={getTradeSide(params.data?.tradeId)}
              isDepth={false}
              pricePrecision={params.data?.pricePrecision ?? 4}
              volumePrecision={params.data?.volumePrecision ?? 0}
              data={params.data}
              align={false}
              isOrganizationOrder={isOrganizationTrade(params.data?.tradeId)}
              displayMode="marketGridBadge"
              contentType="price"
              showOwnershipIcon={false}
            />
          );
        },
      },
      {
        name: "volume",
        headerName: this.props.t("Volume"),
        flex: 1,
        cellRenderer: (params) => {
          return (
            <OrderCell
              value={params.data?.volume}
              type={getTradeSide(params.data?.tradeId)}
              isDepth={false}
              pricePrecision={params.data?.pricePrecision ?? 4}
              volumePrecision={params.data?.volumePrecision ?? 0}
              data={params.data}
              align={false}
              isOrganizationOrder={isOrganizationTrade(params.data?.tradeId)}
              displayMode="marketGridBadge"
              contentType="volume"
              showOwnershipIcon={false}
            />
          );
        },
      },
      {
        name: "tradeConfirmation",
        headerName: this.props.t("TradeConfirmation"),
        flex: 1.5,
        cellRenderer: this.renderConfirmationAction,
      },
    ];

    return columns;
  }

  render() {
    const { marketTrades, tradeOnBehalf } = this.props;
    const { columns, selectedActingOrganizationId, selectedTrade, showConfirmationModal } = this.state;
    const data = _.orderBy(marketTrades, "tradeDate", "desc");

    return (
      <>
        <ThemeableAgGrid
          className="table table-dark table-small trading-grid"
          columnDefs={columns}
          rowData={data}
          animateRows={true}
          domLayout="autoHeight"
          getRowHeight={() => 30}
          rowSelection={false}
        />
        <TradeConfirmationModal
          show={showConfirmationModal}
          onHide={this.closeTradeConfirmation}
          engineTradeId={selectedTrade?.tradeId}
          actingOrganizationId={selectedActingOrganizationId}
          isAdministrator={tradeOnBehalf}
          onSaved={this.loadTradeConfirmationStatuses}
        />
      </>
    );
  }
}

export default withTranslation(["trading"])(MarketTrades);
